import { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tabs,
  Tab,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, configApiRef, fetchApiRef } from '@backstage/core-plugin-api';

interface EnvConfig {
  key: string;
  displayName: string;
  color: string;
  namespace: string;
}

interface ContainerMetrics {
  containerName: string;
  cpuUsage: number;
  cpuLimit: number;
  memUsage: number;
  memLimit: number;
}

interface PodMetrics {
  podName: string;
  containers: ContainerMetrics[];
}

// ── Helpers ──

function parseCpu(value: string): number {
  if (!value) return 0;
  if (value.endsWith('n')) return parseInt(value, 10) / 1_000_000;
  if (value.endsWith('m')) return parseInt(value, 10);
  return parseFloat(value) * 1000;
}

function parseMemory(value: string): number {
  if (!value) return 0;
  if (value.endsWith('Ki')) return parseInt(value, 10) * 1024;
  if (value.endsWith('Mi')) return parseInt(value, 10) * 1024 * 1024;
  if (value.endsWith('Gi')) return parseInt(value, 10) * 1024 * 1024 * 1024;
  if (value.endsWith('Ti')) return parseInt(value, 10) * 1024 * 1024 * 1024 * 1024;
  if (value.endsWith('K') || value.endsWith('k')) return parseInt(value, 10) * 1000;
  if (value.endsWith('M')) return parseInt(value, 10) * 1_000_000;
  if (value.endsWith('G')) return parseInt(value, 10) * 1_000_000_000;
  return parseInt(value, 10);
}

function formatCpu(millicores: number): string {
  if (millicores >= 1000) return `${(millicores / 1000).toFixed(1)}`;
  return `${Math.round(millicores)}m`;
}

function formatMemory(bytes: number): string {
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)}Gi`;
  if (bytes >= 1024 * 1024) return `${Math.round(bytes / (1024 * 1024))}Mi`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)}Ki`;
  return `${bytes}B`;
}

function getProgressColor(percent: number): string {
  if (percent >= 90) return '#f44336';
  if (percent >= 70) return '#ff9800';
  return '#4caf50';
}

const useStyles = makeStyles(theme => ({
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    minHeight: 40,
    '& .MuiTab-root': {
      minHeight: 40,
      fontWeight: 700,
      textTransform: 'uppercase',
      fontSize: '0.75rem',
      letterSpacing: '0.06em',
    },
  },
  table: {
    '& th': {
      fontWeight: 600,
      fontSize: '0.75rem',
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      color: theme.palette.text.secondary,
      borderBottom: `2px solid ${theme.palette.divider}`,
      padding: theme.spacing(1, 2),
    },
    '& td': {
      padding: theme.spacing(1.2, 2),
    },
  },
  podName: {
    fontFamily: 'monospace',
    fontSize: '0.8rem',
    fontWeight: 600,
  },
  progressTrack: {
    height: 8,
    borderRadius: 4,
    backgroundColor: theme.palette.action.disabledBackground,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 4,
    transition: 'width 0.4s ease',
  },
  metricsText: {
    fontFamily: 'monospace',
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
    marginTop: 2,
  },
  empty: {
    textAlign: 'center',
    padding: theme.spacing(4),
  },
  cardTitle: {
    fontWeight: 600,
    fontSize: '1rem',
    padding: theme.spacing(2, 2, 0, 2),
  },
  lastUpdate: {
    fontSize: '0.7rem',
    color: theme.palette.text.hint,
    padding: theme.spacing(0, 2, 1, 2),
  },
}));

export const PodMetricsCard = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const configApi = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);
  const backendBaseUrl = configApi.getString('backend.baseUrl');

  const annotations = entity.metadata.annotations ?? {};
  const k8sId = annotations['backstage.io/kubernetes-id'] ?? entity.metadata.name;
  const labelSelector = annotations['backstage.io/kubernetes-label-selector'];

  const [activeTab, setActiveTab] = useState(0);
  const [envs, setEnvs] = useState<EnvConfig[]>([]);
  const [metrics, setMetrics] = useState<Record<string, PodMetrics[]>>({});
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string | null>>({});
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    try {
      const arr = configApi.getConfigArray('fondative.environments');
      setEnvs(
        arr.map(c => ({
          key: c.getString('key'),
          displayName: c.getString('displayName'),
          color: c.getOptionalString('color') ?? '#1976d2',
          namespace: c.getOptionalString('namespace') ?? 'default',
        })),
      );
    } catch {
      setEnvs([]);
    }
  }, [configApi]);

  const fetchMetrics = useCallback(async () => {
    if (!envs.length) return;
    const selector = labelSelector ?? `app=${k8sId}`;

    for (const env of envs) {
      setLoading(prev => ({ ...prev, [env.key]: true }));
      setErrors(prev => ({ ...prev, [env.key]: null }));

      try {
        // Fetch pods
        const podsRes = await fetchApi.fetch(
          `${backendBaseUrl}/api/proxy/kubernetes/api/api/v1/namespaces/${env.namespace}/pods?labelSelector=${encodeURIComponent(selector)}`,
        );
        if (!podsRes.ok) throw new Error(`Pods: HTTP ${podsRes.status}`);
        const podsData = await podsRes.json();
        const podItems: any[] = podsData.items ?? [];

        if (podItems.length === 0) {
          setMetrics(prev => ({ ...prev, [env.key]: [] }));
          setLoading(prev => ({ ...prev, [env.key]: false }));
          continue;
        }

        const podNames = new Set(podItems.map((p: any) => p.metadata?.name as string));

        // Build limits map: podName -> containerName -> { cpuLimit, memLimit }
        const limitsMap: Record<string, Record<string, { cpuLimit: number; memLimit: number }>> = {};
        for (const pod of podItems) {
          const pName = pod.metadata?.name ?? '';
          limitsMap[pName] = {};
          for (const c of pod.spec?.containers ?? []) {
            limitsMap[pName][c.name] = {
              cpuLimit: parseCpu(c.resources?.limits?.cpu ?? '0'),
              memLimit: parseMemory(c.resources?.limits?.memory ?? '0'),
            };
          }
        }

        // Fetch metrics
        const metricsRes = await fetchApi.fetch(
          `${backendBaseUrl}/api/proxy/kubernetes/api/apis/metrics.k8s.io/v1beta1/namespaces/${env.namespace}/pods`,
        );
        if (!metricsRes.ok) throw new Error(`Metrics: HTTP ${metricsRes.status}`);
        const metricsData = await metricsRes.json();
        const metricsItems: any[] = (metricsData.items ?? []).filter(
          (m: any) => podNames.has(m.metadata?.name),
        );

        const result: PodMetrics[] = metricsItems.map((m: any) => {
          const pName = m.metadata?.name ?? '';
          const containers: ContainerMetrics[] = (m.containers ?? []).map((c: any) => {
            const limits = limitsMap[pName]?.[c.name] ?? { cpuLimit: 0, memLimit: 0 };
            return {
              containerName: c.name,
              cpuUsage: parseCpu(c.usage?.cpu ?? '0'),
              cpuLimit: limits.cpuLimit,
              memUsage: parseMemory(c.usage?.memory ?? '0'),
              memLimit: limits.memLimit,
            };
          });
          return { podName: pName, containers };
        });

        setMetrics(prev => ({ ...prev, [env.key]: result }));
      } catch (err: any) {
        setErrors(prev => ({ ...prev, [env.key]: err.message }));
        setMetrics(prev => ({ ...prev, [env.key]: [] }));
      } finally {
        setLoading(prev => ({ ...prev, [env.key]: false }));
      }
    }
    setLastUpdate(new Date());
  }, [envs, k8sId, labelSelector, backendBaseUrl, fetchApi]);

  // Initial fetch + auto-refresh every 30s
  useEffect(() => {
    fetchMetrics();
    intervalRef.current = setInterval(fetchMetrics, 30000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchMetrics]);

  if (!envs.length) return null;

  const currentEnv = envs[activeTab];
  const currentMetrics = metrics[currentEnv.key] ?? [];
  const isLoading = loading[currentEnv.key] ?? true;
  const currentError = errors[currentEnv.key] ?? null;

  return (
    <Card>
      <Typography className={classes.cardTitle}>
        Utilisation des ressources
      </Typography>
      {lastUpdate && (
        <Typography className={classes.lastUpdate}>
          Dernière mise à jour : {lastUpdate.toLocaleTimeString('fr-FR')} — rafraîchissement auto. 30s
        </Typography>
      )}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        className={classes.tabs}
        indicatorColor="primary"
        textColor="primary"
      >
        {envs.map(env => (
          <Tab
            key={env.key}
            label={`${env.displayName} (${env.namespace})`}
            style={{ color: env.color }}
          />
        ))}
      </Tabs>
      <CardContent style={{ padding: 0 }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : currentError ? (
          <Box className={classes.empty}>
            <Typography color="error">
              Erreur : {currentError}
            </Typography>
          </Box>
        ) : currentMetrics.length === 0 ? (
          <Box className={classes.empty}>
            <Typography color="textSecondary">
              Aucune métrique disponible dans le namespace <strong>{currentEnv.namespace}</strong>
            </Typography>
          </Box>
        ) : (
          <Table className={classes.table} size="small">
            <TableHead>
              <TableRow>
                <TableCell>Pod</TableCell>
                <TableCell style={{ minWidth: 220 }}>CPU</TableCell>
                <TableCell style={{ minWidth: 220 }}>Mémoire</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {currentMetrics.map(pod =>
                pod.containers.map(c => {
                  const cpuPercent = c.cpuLimit > 0 ? (c.cpuUsage / c.cpuLimit) * 100 : 0;
                  const memPercent = c.memLimit > 0 ? (c.memUsage / c.memLimit) * 100 : 0;
                  const cpuColor = getProgressColor(cpuPercent);
                  const memColor = getProgressColor(memPercent);

                  return (
                    <TableRow key={`${pod.podName}-${c.containerName}`} hover>
                      <TableCell>
                        <Typography className={classes.podName}>
                          {pod.containers.length > 1
                            ? `${pod.podName} (${c.containerName})`
                            : pod.podName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {c.cpuLimit > 0 ? (
                          <Box>
                            <Tooltip
                              title={`${formatCpu(c.cpuUsage)} / ${formatCpu(c.cpuLimit)} (${Math.round(cpuPercent)}%)`}
                              arrow
                            >
                              <div className={classes.progressTrack}>
                                <div
                                  className={classes.progressFill}
                                  style={{
                                    width: `${Math.min(cpuPercent, 100)}%`,
                                    backgroundColor: cpuColor,
                                  }}
                                />
                              </div>
                            </Tooltip>
                            <Typography className={classes.metricsText}>
                              {formatCpu(c.cpuUsage)} / {formatCpu(c.cpuLimit)} ({Math.round(cpuPercent)}%)
                            </Typography>
                          </Box>
                        ) : (
                          <Typography className={classes.metricsText}>
                            {formatCpu(c.cpuUsage)} (pas de limite)
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {c.memLimit > 0 ? (
                          <Box>
                            <Tooltip
                              title={`${formatMemory(c.memUsage)} / ${formatMemory(c.memLimit)} (${Math.round(memPercent)}%)`}
                              arrow
                            >
                              <div className={classes.progressTrack}>
                                <div
                                  className={classes.progressFill}
                                  style={{
                                    width: `${Math.min(memPercent, 100)}%`,
                                    backgroundColor: memColor,
                                  }}
                                />
                              </div>
                            </Tooltip>
                            <Typography className={classes.metricsText}>
                              {formatMemory(c.memUsage)} / {formatMemory(c.memLimit)} ({Math.round(memPercent)}%)
                            </Typography>
                          </Box>
                        ) : (
                          <Typography className={classes.metricsText}>
                            {formatMemory(c.memUsage)} (pas de limite)
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                }),
              )}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};
